import { Injectable } from '@angular/core';
import {
  Observable,
  combineLatest,
  debounceTime,
  fromEvent,
  map,
  merge,
  shareReplay,
  withLatestFrom,
} from 'rxjs';

export const ASSET_MAIN_CONTAINER_WIDTH_RATIO = 0.95;
export const ASSET_GROUP_PADDING = 24;
export const ASSET_GROUP_GAP = 16;
export const ASSET_MAIN_CONTAINER_GAP = 64;
export const SCREEN_HORIZONTAL_PADDING = 32;

@Injectable({
  providedIn: 'root',
})
export class AssetUiService {
  assetGroupPadding = ASSET_GROUP_PADDING;
  assetGroupGap = ASSET_GROUP_GAP;
  mainContainerGap = ASSET_MAIN_CONTAINER_GAP;
  screenHorizontalPadding = SCREEN_HORIZONTAL_PADDING;

  setupAssetWidthCalculation(
    androidTvAvailable$: Observable<boolean>,
    isCreationProcess$: Observable<boolean>,
    menuWidth$: Observable<number>,
  ): Observable<number> {
    // base observable for initial width calculation
    const initialWidth$ = combineLatest([
      androidTvAvailable$,
      isCreationProcess$,
      menuWidth$,
    ]).pipe(
      map(([isAvailable, isCreationProcess, menuWidth]) =>
        this.calculateAssetWidth(isAvailable, isCreationProcess, menuWidth),
      ),
    );

    // observable for window resize events
    const resizeWidth$ = fromEvent(window, 'resize').pipe(
      debounceTime(100),
      withLatestFrom(
        combineLatest([androidTvAvailable$, isCreationProcess$, menuWidth$]),
      ),
      map(([, [isAvailable, isCreationProcess, menuWidth]]) =>
        this.calculateAssetWidth(isAvailable, isCreationProcess, menuWidth),
      ),
    );

    // merge both sources to get width updates from both initial state and resize events
    return merge(initialWidth$, resizeWidth$).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  calculateAssetWidth(
    isAndroidTvAvailable: boolean,
    isCreationProcess: boolean,
    menuWidth: number,
  ): number {
    let thinnestColumnInnerWidth;
    let width;

    // creation process
    if (isCreationProcess) {
      if (window.innerWidth > 1200) {
        thinnestColumnInnerWidth =
          (window.innerWidth - this.screenHorizontalPadding * 2 - this.mainContainerGap) *
            this.getThinnestColumnProportion(isAndroidTvAvailable, isCreationProcess) -
          this.assetGroupPadding * 2 -
          this.assetGroupGap;
      } else {
        thinnestColumnInnerWidth =
          window.innerWidth -
          this.screenHorizontalPadding * 2 -
          this.assetGroupPadding * 2 -
          this.assetGroupGap;
      }
      if (!isCreationProcess && !isAndroidTvAvailable && window.innerWidth > 1200) {
        width = thinnestColumnInnerWidth;
      } else if (isCreationProcess && !isAndroidTvAvailable && window.innerWidth > 1800) {
        width = thinnestColumnInnerWidth;
      } else {
        width = thinnestColumnInnerWidth / 2;
      }
    }
    // update process
    else {
      if (window.innerWidth > 1200) {
        if (isAndroidTvAvailable) {
          const totalPadding = 312;
          if (window.innerWidth > 1500) {
            width = (window.innerWidth - menuWidth - totalPadding) / 6;
          } else {
            width = (window.innerWidth - menuWidth - totalPadding) / 4;
          }
        } else {
          const totalPadding = 264;
          width = (window.innerWidth - menuWidth - totalPadding) / 3;
        }
      } else {
        const totalPadding = 200;
        width = (window.innerWidth - menuWidth - totalPadding) / 2;
      }
    }

    if (width < 100) {
      return 100;
    } else if (width > 800) {
      return 800;
    } else {
      return width;
    }
  }

  getThinnestColumnProportion(
    isAndroidTvAvailable: boolean,
    isCreationProcess: boolean,
  ): number {
    if (isAndroidTvAvailable) {
      if (window.innerWidth > 1500) {
        return 1 / 3;
      } else {
        return 1 / 2;
      }
    } else {
      if (isCreationProcess) {
        if (window.innerWidth > 1800) {
          return 1 / 3;
        } else {
          return 1 / 2;
        }
      } else {
        return 1 / 3;
      }
    }
  }

  getRightColumnFlexProportion(
    isAndroidTvAvailable: boolean,
    isCreationProcess: boolean,
  ): number {
    if (isAndroidTvAvailable && window.innerWidth > 1500) {
      return 2;
    } else if (isCreationProcess && !isAndroidTvAvailable && window.innerWidth > 1800) {
      return 2;
    } else {
      return 1;
    }
  }

  getMainContainerFlexDirection(): string {
    if (window.innerWidth > 1200) {
      return 'row';
    } else {
      return 'column';
    }
  }

  setupMainContainerFlexDirection(): Observable<string> {
    // Initial direction based on current window size
    const initialDirection$ = new Observable<string>(observer => {
      observer.next(this.getMainContainerFlexDirection());
    });

    // Direction updates on window resize
    const resizeDirection$ = fromEvent(window, 'resize').pipe(
      debounceTime(100),
      map(() => this.getMainContainerFlexDirection()),
    );

    // Merge both sources to get direction updates from both initial state and resize events
    return merge(initialDirection$, resizeDirection$).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
